import { useState } from 'react'
import * as Yup from 'yup'
import sendTrackingEvent from 'mParticle/sendTrackingEvent'
import { useSubmitRaffleNotifyMutation } from 'api/rafflesApi'
import { emailValidation } from 'shared/addressValidations'

interface UseRaffleNotifyFormOptions {
  /**
   * Translation prefix for error messages
   */
  translationPrefix: string
  /**
   * Fallback error message for submission failures
   */
  submitErrorMessage: string
  /**
   * Source surface for analytics tracking (e.g. 'detail_page',
   * 'index_sidebar', 'pdp_panel'). Used by DRAWING_NOTIFY_SUBMIT
   * and DRAWING_NOTIFY_SUCCESS events.
   */
  trackingSource?: string
  /**
   * Product template slug for analytics tracking. `null` for generic
   * signups from the /drawings sidebar that aren't drawing-specific.
   */
  trackingProductTemplateSlug?: string | null
}

export const useRaffleNotifyForm = (options: UseRaffleNotifyFormOptions) => {
  const { submitErrorMessage, trackingSource, trackingProductTemplateSlug } = options
  const [selectedStore, setSelectedStore] = useState('')
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [submitNotify, { isLoading }] = useSubmitRaffleNotifyMutation()

  const validateEmail = async (value: string) => {
    try {
      await emailValidation().validate(value)
      setEmailError('')
      return true
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        setEmailError(err.message)
      }
      return false
    }
  }

  const handleSubmit = async () => {
    setSuccessMessage('')
    setErrorMessage('')

    const isValid = await validateEmail(email)
    if (!isValid || !selectedStore) return

    sendTrackingEvent('DRAWING_NOTIFY_SUBMIT', {
      product_template_slug: trackingProductTemplateSlug ?? null,
      store_name: selectedStore,
      source: trackingSource ?? null,
    })

    try {
      const result = await submitNotify({
        email,
        source: 'raffle_signup',
        store: selectedStore,
      }).unwrap()
      sendTrackingEvent('DRAWING_NOTIFY_SUCCESS', {
        product_template_slug: trackingProductTemplateSlug ?? null,
        store_name: selectedStore,
        source: trackingSource ?? null,
      })
      setSuccessMessage(result.message)
    } catch {
      setErrorMessage(submitErrorMessage)
    }
  }

  return {
    selectedStore,
    setSelectedStore,
    email,
    setEmail,
    emailError,
    setEmailError,
    successMessage,
    setSuccessMessage,
    errorMessage,
    setErrorMessage,
    isLoading,
    validateEmail,
    handleSubmit,
  }
}
